<script setup lang="ts">
import { formatCepMasked, normalizeCepDigits } from '@solfacil/frontend-utils/cep';
import { computed } from 'vue';

import InfoGridItem from '~/components/shared/InfoGridItem.vue';

import useCustomerRegistrationDetails from '../../queries/useCustomerRegistrationDetails';

interface Props {
  isLoading?: boolean;
  projectId: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const registrationQuery = useCustomerRegistrationDetails(computed(() => props.projectId));
const registrationData = computed(() => registrationQuery.data.value);
const internalIsLoading = computed(
  () => props.isLoading || registrationQuery.isLoading.value || registrationQuery.isFetching.value,
);

const formatBoolean = (value?: boolean) => {
  if (value === undefined || value === null) return null;
  return value ? 'Sim' : 'Não';
};

const installationAddressData = computed(() => {
  const address = registrationData.value?.project?.address;
  if (!address) return [];

  return [
    { label: 'CEP', value: address.zipcode ? formatCepMasked(normalizeCepDigits(address.zipcode)) : null },
    { label: 'Rua', value: address.street || null },
    { label: 'Número', value: address.street_number || null },
    { label: 'Complemento', value: address.complement || null },
    { label: 'Bairro', value: address.neighborhood || null },
    { label: 'Cidade', value: address.city?.name || null },
    { label: 'Estado', value: address.state?.name || null },
    { label: 'Zona Rural', value: formatBoolean(address.is_rural) },
  ].filter((item) => item.value !== null);
});

const hasData = computed(() => installationAddressData.value.length > 0);

const skeletonInstallationAddressData = computed(() => {
  if (!internalIsLoading.value) return [];
  return [
    { label: 'CEP', value: null },
    { label: 'Rua', value: null },
    { label: 'Número', value: null },
    { label: 'Complemento', value: null },
    { label: 'Bairro', value: null },
    { label: 'Cidade', value: null },
    { label: 'Estado', value: null },
    { label: 'Zona Rural', value: null },
  ];
});
</script>

<template>
  <div class="installation-address-content">
    <div v-if="!internalIsLoading && !hasData" class="empty-state">
      <p class="empty-message">Dados ainda não preenchidos</p>
    </div>

    <div v-else class="datagrid">
      <InfoGridItem
        v-for="item in (internalIsLoading ? skeletonInstallationAddressData : installationAddressData)"
        :key="item.label"
        :label="item.label"
        :value="item.value"
        :is-loading="internalIsLoading"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.installation-address-content {
  .datagrid {
    display: grid;
    gap: 0.75rem;
  }

  .empty-state {
    padding: 2rem;
    text-align: center;
  }

  .empty-message {
    color: #94a3b8;
    font-size: 0.9375rem;
    font-style: italic;
    margin: 0;
  }
}
</style>
